/**
 * Time-box an async unit of work so a hung network read can never leave the UI
 * spinning forever. On a *clean* offline drop a fetch rejects quickly, but on a
 * weak/flaky connection it can hang indefinitely — the failure mode behind the
 * original "Loading… forever" bug. runWithTimeout races the work against a
 * deadline and, when the deadline wins, aborts the work (so the dead request is
 * cancelled, not leaked) and rejects with a TimeoutError the caller can show
 * honestly.
 *
 * The work receives the AbortSignal so it can wire it into the underlying fetch
 * (e.g. Supabase's `.abortSignal()`), letting an abort actually cancel the
 * in-flight request rather than just being ignored.
 */

export class TimeoutError extends Error {
  /** The deadline, in ms, that elapsed. */
  readonly ms: number
  constructor(ms: number) {
    super(`Request timed out after ${ms}ms`)
    this.name = 'TimeoutError'
    this.ms = ms
  }
}

export function runWithTimeout<T>(
  run: (signal: AbortSignal) => Promise<T>,
  ms: number,
): Promise<T> {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined

  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const timeout = new TimeoutError(ms)
      // Abort WITH a reason: a bare abort() makes consumers reject with
      // Chromium's "signal is aborted without reason" DOMException.
      controller.abort(timeout)
      reject(timeout)
    }, ms)
  })

  const work = run(controller.signal)
  // If the deadline wins, `work` may still settle (or reject from the abort)
  // later — handle it on a side branch so it never surfaces as an unhandled
  // rejection. The race below sees the original settlement independently.
  void work.catch(() => {})

  return Promise.race([work, deadline]).finally(() => {
    if (timer) clearTimeout(timer)
  }) as Promise<T>
}
